import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FiCopy, FiCheck } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  RetriveWalletReportService,
  getfundRequestStatusService,
} from "../../../services/walletservice";
import Loader from "../../../components/Loader/Loader";
import PaginationData from "../../../components/PaginationData";
import { FaEye } from "react-icons/fa6";
import NetworkBackground from "../../LoginPage/NetworkBackground.jsx";
import { Copy, Target, Wallet, Wallet2, X } from "lucide-react";
import { showSuccessToast } from "../../../utils/toastUtils.js";
import { RetriveServiceReportService3 } from "../../../services/apiaddservice.js";

const FundManagementReport = () => {
  const [walletReportData, setWalletReportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderId, setOrderId] = useState([]);
  const [paginationData, setPaginationData] = useState();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModelReqOpen, setJsonModelReqOpen] = useState(false);
  const [jsonResponseData, setJsonResponseData] = useState(null);
  const [jsonRequestData, setJsonRequestData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [serviceList, setServiceList] = useState([]);
  const [backupServiceList, setBackupServiceList] = useState([]);
  const [debouncedServiceSearch, setDebouncedServiceSearch] = useState(null);
  const [showServiceList, setShowServiceList] = useState(false);

  const [filterData, setFilterData] = useState({
    from_date: "",
    to_date: "",
    status: "",
    user_id: "",
    client_ref_num: "",
    request_id: "",
    page: "",
    total_data: "",
    service_name: "",
    per_page: 40,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fetChWalletData = async () => {
    setIsLoading(true);
    setLoading(true);

    try {
      const response = await RetriveWalletReportService(
        filterData,
        currentPage,
      );

      if (response.status.code === 200) {
        const reports = response?.data?.data;
        setWalletReportData(reports);
        setSummary(response?.data?.summary || null);

        // Optionally get the client_txn_id from the first QR record
        const unsettledQr = reports.find(
          (item) =>
            item.status?.value?.toLowerCase() === "unsettled" &&
            item.mode?.toLowerCase() === "qr" &&
            item.qr_payment?.client_txn_id,
        );

        if (unsettledQr) {
          setOrderId(unsettledQr.qr_payment.client_txn_id);
        } else {
          setOrderId(null); // or ""
        }

        setPaginationData(response.data.pagination);
      } else {
        setWalletReportData([]);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setWalletReportData([]);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handlerFilterSearch = async () => {
    setIsLoading(true);
    if (searchText === " ") {
      fetChWalletData();
      setIsLoading(false);
      return;
    }

    const filterData = walletReportData.filter((curr) =>
      curr.user.user_id.includes(searchText),
    );
    setWalletReportData(filterData);
    setIsLoading(false);
    return;
  };
  const handleSetPage = (page) => {
    setCurrentPage(page);
  };
  useEffect(() => {
    fetChWalletData();
  }, [currentPage]);

  useEffect(() => {
    handlerFilterSearch();
  }, [searchText]);

  const handleGetFundStatus = async (orderId) => {
    if (!orderId) return;

    try {
      const response = await getfundRequestStatusService(orderId);
      const rawStatus =
        response?.data?.[0]?.status?.value ||
        response?.status?.message?.toLowerCase() ||
        null;

      const newStatusValue = rawStatus?.includes("failed")
        ? "failed"
        : rawStatus?.includes("pending")
          ? "pending"
          : rawStatus?.includes("unsettled")
            ? "unsettled"
            : rawStatus;

      if (newStatusValue) {
        setWalletReportData((prev) =>
          prev.map((item) =>
            item?.qr_payment?.client_txn_id === orderId
              ? {
                  ...item,
                  qr_payment: {
                    ...item.qr_payment,
                    status: {
                      ...item.qr_payment.status,
                      value: newStatusValue,
                    },
                  },
                }
              : item,
          ),
        );
      } else {
        console.warn("Status not updated – no valid value received");
      }
    } catch (error) {
      console.error("Error checking fund status:", error);
    }
  };

  const waitForNextPaint = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        setTimeout(resolve, 0);
      }
    });

  const parseJsonPayload = (jsonString, type) => {
    try {
      return jsonString ? JSON.parse(jsonString) : [];
    } catch (err) {
      console.error(`JSON parse error for ${type}:`, err);
      return jsonString || [];
    }
  };

  const handleOpenResponseModal = async (report) => {
    setIsLoading(true);
    try {
      await waitForNextPaint();
      const responseString = report?.api_response?.[0]?.response_data;
      const parsedResponseData = parseJsonPayload(responseString, "response");
      setJsonResponseData(parsedResponseData);
      setJsonModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRequestModal = async (report) => {
    setIsLoading(true);
    try {
      await waitForNextPaint();
      const requestString = report?.api_response?.[0]?.request_data;
      const parsedRequestData = parseJsonPayload(requestString, "request");
      setJsonRequestData(parsedRequestData);
      setJsonModelReqOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("copied"));
  };

  useEffect(() => {
    // if (serviceList?.length === 0) return;
    if (filterData?.service_name === "") setServiceList(backupServiceList);

    const target = filterData?.service_name?.toLowerCase()?.trim();

    const match = backupServiceList?.filter((item, idx) => {
      return item?.service_name?.toLowerCase().includes(target);
    });
    console.log("target", target);
    console.log("match", match);
    console.log("service name", filterData?.service_name);

    // if (match?.length > 0) {
    setServiceList(match);
    // } else {
    //   setServiceList(backupServiceList);
    // }
  }, [filterData?.service_name]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await RetriveServiceReportService3();

        if (res?.status?.code === 200) {
          setServiceList(res?.data?.services?.data);
          setBackupServiceList(res?.data?.services?.data);
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchServices();
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2 }}
      >
        {(isLoading || loading) && <Loader />}
        <div className="bg-white shadow-md rounded-lg mt-8 p-0.5">
          <div className="flex items-center gap-3.5 px-2 py-1 ">
            {/* Modern Icon Wrapper */}
            <div className="p-2 bg-indigo-50 text-[#2563EB] rounded-xl border border-indigo-100/50 shadow-sm shrink-0">
              <Wallet2 />
            </div>

            {/* Typography */}
            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                Wallet Transaction Report
              </h3>
              <p className="text-xs font-medium text-gray-500">
                Monitor credits, debits, and historical statements
              </p>
            </div>
          </div>
          <ToastContainer position="top-right" />
          <div>
            <div className="w-full bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col gap-4 mb-4">
              {/* --- Filters Section (Moved to Top & Tightened) --- */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-3 items-end">
                {/* From Date */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="from_date"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    name="from_date"
                    id="from_date"
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={filterData.from_date}
                    onChange={handleChange}
                  />
                </div>

                {/* To Date */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="to_date"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    name="to_date"
                    id="to_date"
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={filterData.to_date}
                    onChange={handleChange}
                  />
                </div>

                {/* User ID */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="user_id"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    User ID
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    id="user_id"
                    placeholder="e.g. AP10023"
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                    value={filterData.user_id.toUpperCase()}
                    onChange={handleChange}
                  />
                </div>

                {/* Service Name */}
                <div className="flex flex-col  w-full gap-1">
                  <label
                    htmlFor="service_name"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    Service Name
                  </label>
                  <input
                    onFocus={() => setShowServiceList(true)}
                    // onBlur={() => setShowServiceList(false)}
                    type="search"
                    autoComplete="off"
                    name="service_name"
                    id="service_name"
                    placeholder="Service Name"
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                    value={filterData.service_name}
                    onChange={handleChange}
                  />

               {/* Dropdown Container */}
{serviceList?.length > 0 && showServiceList && (
  <div className="relative">
    <div className="absolute top-full left-0 mt-2 w-72 z-30 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
        <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
          Select Service
        </h2>
        <button 
          onClick={() => setShowServiceList(false)}
          className="p-1 rounded-lg text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Service List Options */}
      <div className="py-1 max-h-60 overflow-y-auto divide-y divide-zinc-50 dark:divide-zinc-800/50">
        {serviceList.map((item, idx) => (
          <button
            key={item?.id || idx} // Highly recommended to use a unique ID instead of index if available
            onClick={() => {
              setFilterData((prev) => ({
                ...prev,
                service_name: item?.service_name,
              }));
              setShowServiceList(false);
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors block layout-fixed truncate"
          >
            {item?.service_name}
          </button>
        ))}
      </div>

    </div>
  </div>
)}
                </div>

                {/* Status */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="status"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    id="status"
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={filterData.status}
                    onChange={handleChange}
                  >
                    <option value="">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Client Ref Num */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="client_ref_num"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    Client Ref Num.
                  </label>
                  <input
                    type="text"
                    name="client_ref_num"
                    id="client_ref_num"
                    placeholder="17834969..."
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                    value={filterData.client_ref_num}
                    onChange={handleChange}
                  />
                </div>

                {/* Request ID */}
                <div className="flex flex-col w-full gap-1">
                  <label
                    htmlFor="request_id"
                    className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    Request ID
                  </label>
                  <input
                    type="text"
                    name="request_id"
                    id="request_id"
                    placeholder="7c7fb3e0-..."
                    className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                    value={filterData.request_id}
                    onChange={handleChange}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex flex-col w-full">
                  <button
                    type="button"
                    onClick={fetChWalletData}
                    className="w-full py-2 px-4 rounded-lg text-white text-sm font-medium bg-[#2563EB] hover:bg-indigo-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-all active:scale-[0.98]"
                  >
                    Submit Filters
                  </button>
                </div>
              </div>

              

              {/* --- Compact Financial Summary Section (Moved to Bottom) --- */}
              <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center gap-x-8 gap-y-3 pt-3 mt-1 border-t border-gray-100">
                {/* Current Month Ribbon */}
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-semibold text-gray-500">
                    CURRENT MONTH:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-md">
                      Cr: ₹ {summary?.current_month?.credit ?? "0.00"}
                    </span>
                    <span className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-100 px-2 py-1 rounded-md">
                      Dr: ₹ {summary?.current_month?.debit ?? "0.00"}
                    </span>
                  </div>
                </div>

                {/* Last Month Ribbon */}
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-semibold text-gray-500">
                    LAST MONTH:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-md">
                      Cr: ₹ {summary?.last_month?.credit ?? "0.00"}
                    </span>
                    <span className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-100 px-2 py-1 rounded-md">
                      Dr: ₹ {summary?.last_month?.debit ?? "0.00"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="w-1/3">
                {/* <label htmlFor="total_data">Select Total Data</label> */}
                <select
                  name="total_data"
                  id="total_data"
                  className=" py-2 px-3 text-sm rounded-lg mb-2 border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={filterData.total_data}
                  onChange={handleChange}
                >
                  <option value="" disabled selected>
                    Select Total Data
                  </option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              {/* <div className="w-1/5">
                  <input type="text" placeholder="Service / Name" className="border px-2 py-1 rounded-md border-blue-600 w-full"
                      value={filterData.service_name}
                      onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                  />
              </div> */}
            </div>

            <div className="overflow-x-auto p-0">
              <table className="min-w-full divide-y divide-gray-200 ">
                <thead className="bg-gray-50 top-0">
                  <tr className="bg-blue-700 text-center text-white">
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      User ID
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Gst %
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Gst Value
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Mode
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Vendor Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Transaction Id
                    </th>

                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Utr
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1 font-semibold text-white tracking-wider border-r"
                    >
                      Scope
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Txn Type
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Wallet type
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Business Wallet
                    </th>

                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Service Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Remarks
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Source Req/Res
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      IDSPay Req/Res
                    </th>
                    {/* <th
                        scope="col"
                        className="px-2 text-center py-3  text-xs font-medium text-white uppercase tracking-wider">Transacted By
                    </th> */}
                  </tr>
                </thead>
                <tbody>
                  {walletReportData.length > 0 ? (
                    walletReportData.map((report, index) => (
                      <tr key={index}>
                        <td className="text-center p-1 whitespace-nowrap text-xs text-gray-500 border-b-2 border-r">
                          {report?.user?.user_id || "N/A"}
                        </td>
                        <td className="text-center p-1 whitespace-nowrap text-xs font-bold text-gray-500 border-b-2 border-r">
                          {report?.user?.name || "N/A"}
                        </td>
                        <td className="text-center p-1 whitespace-nowrap text-sm border-b-2 border-r">
                          <span
                            className={
                              report?.factor === "Dr"
                                ? "text-red-500 font-semibold"
                                : "text-green-500 font-semibold"
                            }
                          >
                            {report?.amount || "N/A"}
                          </span>
                        </td>

                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {report?.gst_percentage || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap font-sm text-green-500 font-bold border-b-2">
                          {report?.gst_value || "N/A"}
                        </td>
                        <td className="text-center border-r p-1 whitespace-nowrap font-sm text-gray-500 border-b-2">
                          {report?.mode || "Manual Deposit"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2 text-xs">
                          {report?.source?.vendor?.name || "N/A"}
                        </td>
                        <td className="text-center border-r p-1 whitespace-nowrap font-sm text-gray-500 border-b-2">
                          {report?.transaction_id || "Manual Deposit"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {" "}
                          {report?.transaction_ids[0]?.utr || "N/A"}{" "}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2 text-xs">
                          {report?.source?.assignment?.scope || "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm border-b-2">
                          {report?.factor === "Dr" ? (
                            <span className="text-red-400 font-bold">
                              Debit
                            </span>
                          ) : (
                            <span className="text-green-500 font-bold">
                              {report?.factor === "Cr"
                                ? "Credit"
                                : report?.factor || "N/A"}
                            </span>
                          )}
                        </td>

                        {/* <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">{report?.user.balance || "N/A"}</td> */}
                        <td className="text-center border-r p-1  whitespace-nowrap text-xs text-green-500 font-bold border-b-2">
                          {report?.transaction_type || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {report?.balance_after || "N/A"}
                        </td>

                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-gray-500 border-b-2">
                          {report?.source?.service_name || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap text-xs text-blue-500 border-b-2 font-semibold">
                          {report?.narration &&
                          report?.narration !== "No narration provided." &&
                          report?.narration !== "No narration provided"
                            ? report.narration
                            : report?.remarks &&
                                report?.remarks !== "No narration provided." &&
                                report?.remarks !== "No narration provided"
                              ? report.remarks
                              : "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-xs text-gray-500 border-b-2">
                          {report?.transaction_date || report?.created_at
                            ? new Date(
                                report?.transaction_date || report?.created_at,
                              ).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2">
                          <button
                            className={`rounded-md text-white  px-2 flex items-center justify-center gap-1 ${
                              (report?.mode?.toLowerCase() === "qr"
                                ? report?.qr_payment?.status?.value?.toLowerCase()
                                : report?.status?.value?.toLowerCase()) ===
                              "failed"
                                ? "bg-red-500"
                                : (report?.mode?.toLowerCase() === "qr"
                                      ? report?.qr_payment?.status?.value?.toLowerCase()
                                      : report?.status?.value?.toLowerCase()) ===
                                    "pending"
                                  ? "bg-yellow-500"
                                  : (report?.mode?.toLowerCase() === "qr"
                                        ? report?.qr_payment?.status?.value?.toLowerCase()
                                        : report?.status?.value?.toLowerCase()) ===
                                      "unsettled"
                                    ? "bg-blue-500"
                                    : "bg-green-500"
                            }`}
                          >
                            {report?.mode?.toLowerCase() === "qr"
                              ? report?.qr_payment?.status?.value || "N/A"
                              : report?.status?.value || "N/A"}

                            {report?.mode?.toLowerCase() === "qr" &&
                              report?.qr_payment?.status?.value?.toLowerCase() ===
                                "unsettled" &&
                              report?.qr_payment?.client_txn_id && (
                                <span
                                  onClick={() =>
                                    handleGetFundStatus(
                                      report.qr_payment.client_txn_id,
                                    )
                                  }
                                  className="ml-1 cursor-pointer text-xl"
                                  title="Check QR Status"
                                >
                                  🔄
                                </span>
                              )}
                          </button>
                        </td>

                        <td className="p-1 border hidden lg:table-cell text-center">
                          <FaEye
                            className="text-blue-600 hover:text-blue-800 cursor-pointer ml-2"
                            size={28}
                            onClick={() => handleOpenResponseModal(report)}
                          />
                        </td>

                        <td className="p-1 border hidden lg:table-cell text-center">
                          <FaEye
                            className="text-blue-600 hover:text-blue-800 cursor-pointer ml-2"
                            size={28}
                            onClick={() => handleOpenRequestModal(report)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center text-gray-500">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center my-4">
              <PaginationData
                currentPage={currentPage}
                lastPage={paginationData?.last_page ?? 1}
                handlePage={handleSetPage}
              />
            </div>
          </div>
        </div>

        <div className="p-2">
          {/* Modal */}
          {jsonModalOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <NetworkBackground />
              <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200">
                {/* Header */}
                <div className="flex justify-between items-center bg-blue-600 text-white px-2 py-2 rounded-t-2xl">
                  <h2 className="text-lg font-semibold">📄 API Details</h2>
                  <button
                    onClick={() => setJsonModalOpen(false)}
                    className="text-white hover:text-gray-200 transition text-xl font-bold"
                  >
                    ✕
                  </button>
                </div>

                {/* Response Data Section */}
                {jsonResponseData && (
                  <div className="p-4 relative">
                    <h3 className="text-md flex justify-between items-center font-semibold text-green-600 mb-2">
                      📥 Response Data
                      <button
                        className="bg-green-100 text-green-800 p-2 border border-green-500 rounded-lg hover:border-blue-500  hover:bg-blue-600 hover:text-white"
                        onClick={() => handleCopy(jsonResponseData)}
                      >
                        <Copy size={20} />
                      </button>
                    </h3>
                    <pre className="bg-gray-900 text-green-400 text-sm rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed shadow-inner">
                      {JSON.stringify(jsonResponseData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 px-2 py-1 border-t bg-gray-50 rounded-b-2xl">
                  <button
                    onClick={() => setJsonModalOpen(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {jsonModelReqOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <NetworkBackground />
              <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200">
                {/* Header */}
                <div className="flex justify-between items-center bg-blue-600 text-white px-2 py-2 rounded-t-2xl">
                  <h2 className="text-lg font-semibold">📄 API Details</h2>
                  <button
                    onClick={() => setJsonModelReqOpen(false)}
                    className="text-white hover:text-gray-200 transition text-xl font-bold"
                  >
                    ✕
                  </button>
                </div>

                {/* Request Data Section */}
                {jsonRequestData && (
                  <div className="p-4 relative border-b border-gray-300">
                    <h3 className="text-md flex justify-between items-center font-semibold text-blue-600 mb-2">
                      📤 Request Data
                      <button
                        className="bg-green-100 text-green-800 p-2 border border-green-500 rounded-lg hover:border-blue-500  hover:bg-blue-600 hover:text-white"
                        onClick={() => handleCopy(jsonRequestData)}
                      >
                        <Copy size={20} />
                      </button>
                    </h3>
                    <pre className="bg-gray-800 text-yellow-400 text-sm rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed shadow-inner">
                      {JSON.stringify(jsonRequestData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 px-2 py-1 border-t bg-gray-50 rounded-b-2xl">
                  <button
                    onClick={() => setJsonModelReqOpen(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};
export default FundManagementReport;
